import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { InquiryForm } from './InquiryForm';
import { media } from '../data/site';
type ServiceLandingHeroProps = {
  id: string;
  eyebrow: string;
  title: string;
  intro: string;
  image?: string;
  imagePosition?: string;
  alt?: string;
  tone?: 'ivory' | 'olive';
  secondaryAction?: {
    label: string;
    href: string;
  };
};

/**
 * Full-section photographic service hero with a single foreground inquiry form.
 * DOM/phone order: eyebrow + H1 → form → introduction/action.
 */
export function ServiceLandingHero({
  id,
  eyebrow,
  title,
  intro,
  image,
  imagePosition,
  secondaryAction
}: ServiceLandingHeroProps) {
  const titleId = `${id}-title`;
  return <section id={id} aria-labelledby={titleId} className="relative isolate bg-olive bg-cover bg-[position:center_60%] py-10 text-ivory md:py-14 lg:py-16" style={{
    backgroundImage: `url("${image || media.landscapeHero}")`,
    backgroundPosition: imagePosition
  }} data-id="element-244" data-mp="i1rhls7mopo4">
      <div aria-hidden="true" className="service-landing-overlay pointer-events-none absolute inset-0" data-id="element-245" data-mp="18psm0xey02si" />
      <div className="site-container relative grid gap-8 lg:grid-cols-12 lg:grid-rows-[auto_1fr] lg:gap-x-14 lg:gap-y-7" data-id="element-246" data-mp="1dovoqb7g9wxb">
        <div className="lg:col-span-6 lg:row-start-1" data-id="element-247" data-mp="1ilm5z1uzqykb">
          <p className="eyebrow leading-relaxed text-ivory/85" data-id="element-248" data-mp="1ne5gfpydtdkw">{eyebrow}</p>
          <h1 id={titleId} className="mt-5 font-display text-[40px] font-medium leading-[1.04] tracking-[-0.01em] sm:text-[52px] lg:text-[60px]" data-id="element-249" data-mp="1scqkk0gr8hem">
            {title}
          </h1>
        </div>

        <div id="service-inquiry" className="scroll-mt-[88px] border border-ivory/25 bg-olive/60 p-1.5 sm:p-2 lg:col-span-6 lg:col-start-7 lg:row-span-2 lg:row-start-1 lg:self-start lg:scroll-mt-[108px] xl:col-span-5 xl:col-start-8" data-id="element-250" data-mp="21d58z8p0mcmc">
          <InquiryForm compact headingLevel="h2" data-id="element-251" data-mp="bygxd9o90ypd" />
        </div>

        <div className="lg:col-span-6 lg:row-start-2" data-id="element-252" data-mp="bz0ckimil8zp">
          <p className="max-w-[560px] text-[16px] leading-relaxed text-ivory/90 sm:text-[17px]" data-id="element-253" data-mp="3x99xjiuosc2">
            {intro}
          </p>
          {secondaryAction ? <a href={secondaryAction.href} className="focus-ring group mt-6 inline-flex min-h-[44px] items-center gap-2 text-[15px] font-medium text-ivory" data-id="element-254" data-mp="3w3xzwzrf43o1">
              <span className="border-b border-ivory/50 pb-0.5 transition-colors duration-150 group-hover:border-ivory" data-id="element-255" data-mp="8z8douii5r9j">
                {secondaryAction.label}
              </span>
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-256" data-mp="iut9gawz9vau" />
            </a> : null}
        </div>
      </div>
    </section>;
}